/**
 * The API permissions Auth0 puts in the access token (RBAC, "Add Permissions in the Access Token"):
 * a `permissions` claim listing strings such as `read:import`. viewer-api checks the same strings with
 * `hasAuthority(...)`, so reading them here only decides what the viewer SHOWS — the API still refuses
 * whatever the token does not allow.
 */
export const PERMISSIONS = {
  READ_IMPORT: 'read:import',
  CREATE_IMPORT: 'create:import',
  ADMIN_MIGRATION: 'admin:migration',
  LIST_LABEL_TASK: 'list:labelTask',
  READ_LABEL_TASK: 'read:labelTask',
  CREATE_LABEL_TASK: 'create:labelTask',
  /** A task's reload, and stopping a computation of its items that will not finish. */
  UPDATE_LABEL_TASK: 'update:labelTask',
  READ_PREVIEW: 'read:preview',
  /** Prediction products and imports, and their previews. */
  READ_PREDICTION: 'read:prediction',
  /** Products: create, rename, delete; and the decisions on an import. */
  CREATE_PREDICTION: 'create:prediction',
  /** A prediction file's upload and its import into a product — the AI team's machine client holds it too. */
  IMPORT_PREDICTION: 'import:prediction',
  READ_LABEL_PASS: 'read:labelPass',
  FIX_LABEL_PASS: 'fix:labelPass',
  /** Every role's: one's own tasks (`GET /api/label-tasks/me`) and notifications. */
  LIST_OWN_LABEL_TASK: 'list:ownLabelTask',
  READ_NOTIFICATION: 'read:notification',
} as const;

/**
 * The viewer's modes (`/ohif/<mode>`) that every call of theirs needs one permission for, beyond having a role;
 * the other modes open on task membership, which viewer-api checks call by call.
 */
export const MODE_PERMISSIONS: Record<string, string> = {
  preview: PERMISSIONS.READ_PREVIEW,
  correction: PERMISSIONS.FIX_LABEL_PASS,
};

/** The worklist's pages that show one endpoint's data, and that endpoint's permission. */
export const PAGE_PERMISSIONS: Record<string, string> = {
  '/my-label-tasks': PERMISSIONS.LIST_OWN_LABEL_TASK,
  '/notifications': PERMISSIONS.READ_NOTIFICATION,
};

/**
 * The permission a path needs beyond a role: its page's, or its viewer mode's, when it has one. The backoffice
 * checks its own (RequirePermission), section by section.
 */
export function permissionRequiredFor(pathname: string): string | undefined {
  const mode = pathname.match(/^\/ohif\/([^/?#]+)/)?.[1];
  return mode
    ? MODE_PERMISSIONS[mode]
    : PAGE_PERMISSIONS[pathname.replace(/\/+$/, '')];
}

/**
 * The claim Auth0's post-login action writes the user's role names to ("Data Manager", "Labeler", …). An account
 * nobody has given a role yet has an empty list: viewer-api refuses all its calls, the viewer shows /forbidden.
 */
export const ROLES_CLAIM = 'https://gleamer.ai/roles';

/**
 * The backoffice's sections' permissions: a user holding any of them sees the backoffice (the header's link,
 * the home's shortcut, its layout); each section checks its own, and viewer-api checks every call.
 */
export const BACKOFFICE_PERMISSIONS: string[] = [
  PERMISSIONS.LIST_LABEL_TASK,
  PERMISSIONS.READ_IMPORT,
  PERMISSIONS.READ_PREDICTION,
  PERMISSIONS.ADMIN_MIGRATION,
];

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS] | string;

function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  // The payload is UTF-8; atob gives one char per byte.
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** The payload of a JWT, unverified — the API verifies it; the viewer only reads it. `null` if it is not a JWT. */
export function decodeJwtPayload(token: string | null | undefined): Record<string, unknown> | null {
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

export function rolesFromToken(token: string | null | undefined): string[] {
  const roles = decodeJwtPayload(token)?.[ROLES_CLAIM];
  return Array.isArray(roles) ? roles.filter(r => typeof r === 'string') : [];
}

export function permissionsFromToken(token: string | null | undefined): string[] {
  const permissions = decodeJwtPayload(token)?.permissions;
  return Array.isArray(permissions)
    ? permissions.filter(p => typeof p === 'string')
    : [];
}
